/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import moment from 'moment-timezone';

import {
  PERSIST_DATE_FMT,
  DISPLAY_DATE_FMT,
  PERSIST_DATETIME_FMT,
  DISPLAY_DATETIME_FMT,
  DISPLAY_TIME_FMT
} from '../Constants';

export function getFiscalYear() {
  const today = moment();
  const year = today.year();

  // If October, use next year
  if (today.month() >= 9) {
    return year + 1;
  } else {
    return year;
  }
}

/**
 * Parses a date or datetime string into a Moment object according to the date
 * format. Expects local time. Returns null if the conversion is not successful.
 * @param {string} dateStr target date being parsed
 * @param {string} formatStr date format for the target date string
 * @returns {(moment.Moment|null)}
 */
export function parse(dateStr, formatStr) {
  if (dateStr) {
    const date = moment(dateStr, formatStr);
    if (date.isValid()) {
      return date;
    }
  }
  return null;
}

/**
 * Parses a date or datetime string into a Moment object according to the date
 * format. Expects utc time. Returns null if the conversion is not successful.
 * @param {string} dateStr target date being parsed
 * @param {string} formatStr date format for the target date string
 * @returns {(moment.Moment|null)}
 */
export function parseUtc(dateStr, formatStr) {
  if (dateStr) {
    const date = moment.utc(dateStr, formatStr);
    if (date.isValid()) {
      return date.local();
    }
  }
  return null;
}

/**
 * Parses a date string in the "YYYY-MM-DD" format into a Moment object. Returns
 * null if the conversion is not successful.
 * @param {string} dateStr target server date being parsed
 * @returns {(moment.Moment|null)}
 */
export function parseServerDate(dateStr) {
  return parseUtc(dateStr, PERSIST_DATE_FMT);
}

/**
 * Parses a datetime string in the "YYYY-MM-DDTHH:mm:ss" format into a Moment
 * object. Returns null if the conversion is not successful.
 * @param {string} dateTimeStr target server datetime being parsed
 * @returns {(moment.Moment|null)}
 */
export function parseServerDateTime(dateTimeStr) {
  return parseUtc(dateTimeStr, PERSIST_DATETIME_FMT);
}

/**
 * Parses a date string in the "MM/DD/YYYY" format into a Moment object. Returns
 * null if the conversion is not successful.
 * @param {string} dateStr target display date being parsed
 * @returns {(moment.Moment|null)}
 */
export function parseDisplayDate(dateStr) {
  return parse(dateStr, DISPLAY_DATE_FMT);
}

/**
 * Parses a datetime string in the "MM/DD/YYYY hh:mm a" format into a Moment
 * object. Returns null if the conversion is not successful.
 * @param {string} dateTimeStr target display datetime being parsed
 * @returns {(moment.Moment|null)}
 */
export function parseDisplayDateTime(dateTimeStr) {
  return parse(dateTimeStr, DISPLAY_DATETIME_FMT);
}

/**
 * Formats a Moment object into a string according to the date format. Returns
 * null if the format is not successful.
 * @param {moment.Moment} dateMoment target Moment object being formatted
 * @param {string} targetFormat date format to used to format the Moment object
 * @returns {(string|null)}
 */
export function format(dateMoment, targetFormat) {
  if (!_.isString(targetFormat)) {
    throw new Error('Cannot call format without providing a valid targetFormat string.');
  }
  if (moment.isMoment(dateMoment) && dateMoment.isValid()) {
    return dateMoment.format(targetFormat);
  }
  return null;
}

/**
 * Formats a Moment object into a string according to the date format. Returns
 * null if the format is not successful.
 * @param {moment.Moment} dateMoment target Moment object being formatted
 * @param {string} targetFormat date format to used to format the Moment object
 * @returns {(string|null)}
 */
export function formatUtc(dateMoment, targetFormat) {
  if (!_.isString(targetFormat)) {
    throw new Error('Cannot call format without providing a valid targetFormat string.');
  }
  if (moment.isMoment(dateMoment) && dateMoment.isValid()) {
    return dateMoment.utc().format(targetFormat);
  }
  return null;
}

/**
 * Formats a Moment object into a string using the "MM/DD/YYYY" format. Returns
 * null if the conversion is not successful.
 * @param {moment.Moment} dateMoment target moment object being formatted
 * @returns {(string|null)}
 */

export function formatDisplayDate(dateMoment) {
  if (moment.isMoment(dateMoment) && dateMoment.isValid()) {
    return dateMoment.format(DISPLAY_DATE_FMT);
  } else {
    return moment(dateMoment).format(DISPLAY_DATE_FMT);
  }
}

/**
 * Formats a Moment object into a string using the "MM/DD/YYYY hh:mm z" format.
 * Returns null if the conversion is not successful.
 * @param {moment.Moment} dateMoment target moment object being formatted
 * @returns {(string|null)}
 */

export function formatDisplayDateTime(dateMoment) {
  if (moment.isMoment(dateMoment) && dateMoment.isValid()) {
    return dateMoment.tz('America/New_York').format(DISPLAY_DATETIME_FMT);
  } else {
    return moment(dateMoment).tz('America/New_York').format(DISPLAY_DATETIME_FMT);
  }
}

/**
 * Formats a Moment object into a string using the "hh:mm z" format.
 * Returns null if the conversion is not successful.
 * @param {moment.Moment} dateMoment target moment object being formatted
 * @returns {(string|null)}
 */

export function formatDisplayTime(dateMoment) {
  if (moment.isMoment(dateMoment) && dateMoment.isValid()) {
    return dateMoment.tz('America/New_York').format(DISPLAY_TIME_FMT);
  } else {
    return moment(dateMoment).tz('America/New_York').format(DISPLAY_TIME_FMT);
  }
}

/**
 * Formats a Moment object into a string using the "YYYY-MM-DD" format. Returns
 * null if the conversion is not successful.
 * @param {moment.Moment} dateMoment target moment object being formatted
 * @returns {(string|null)}
 */
export function formatServerDate(dateMoment) {
  return formatUtc(dateMoment, PERSIST_DATE_FMT);
}

/**
 * Formats a Moment object into a string using the "YYYY-MM-DDTHH:mm:ss" format.
 * Returns null if the conversion is not successful.
 * @param {moment.Moment} dateMoment target moment object being formatted
 * @returns {(string|null)}
 */
export function formatServerDateTime(dateMoment) {
  return formatUtc(dateMoment, PERSIST_DATETIME_FMT);
}

/**
 * Iterates through source object, parses and formats supplied keys.
 * @param {string[]} sourceObj
 * @param {Object} options Values that control how the address should be converted to a string.
 * @param {string[]} [options.paths] The format to use to parse all provided
 * @param {string} [options.sourceFormat] The format to use to parse all provided
 * @param {string} [options.targetFormat] The format to use to parse all provided
 */
export function parseAndFormatProperties(sourceObj, options) {
  if (!_.isArray(options.paths) || _.isEmpty(options.paths) || _.every(options.paths, _.isString)) {
    throw new Error('A valid array of strings with JavaScript paths must be provided via options.paths.');
  }
  if (!_.isString(options.sourceFormat) || _.isEmpty(options.sourceFormat)) {
    throw new Error('A valid sourceFormat string must be provided via options.sourceFormat.');
  }
  if (!_.isString(options.targetFormat) || _.isEmpty(options.targetFormat)) {
    throw new Error('A valid targetFormat string must be provided via options.targetFormat.');
  }
  const { paths, sourceFormat, targetFormat } = options;
  for (const path of paths) {
    const rawValue = _.get(sourceObj, path);
    const parsedValue = parse(rawValue, sourceFormat);
    const formattedValue = format(parsedValue, targetFormat);
    _.set(sourceObj, path, formattedValue);
  }
}
